/**
 * TICKETA — unified Kitchen / Station ticket template (Feb 2026).
 *
 * Single layout shared across every order source (Retail, Restaurant,
 * Delivery). Designed for fast back-of-house reading: no prices, big
 * order number, prominent station header. Mirrors the ergonomics of
 * a hand-written chit.
 *
 * Layout (top → bottom, 76-mm thermal):
 *   1. Station / post name (e.g. «ΜΠΑΡ», «ΨΗΣΤΑΡΙΑ») — BOLD LARGE, left
 *   2. «ΤΜΗΜΑ: RETAIL | RESTAURANT | DELIVERY» — left
 *   3. ΤΡΑΠΕΖΙ name (Restaurant) OR «Αρ. Παραγγελίας» (Retail/Delivery)
 *      — VERY LARGE BOLD, right-aligned
 *   4. Operator name — right-aligned
 *   5. Datetime (ΗΗ/ΜΜ/ΕΕ ΩΩ:ΛΛ) on the left + order # on the right,
 *      same row, both bold
 *   6. 2 blank rows
 *   7. Horizontal separator
 *   8. Items: «KKK × Description» — BOLD; per-item comments below as
 *      «· comment» one size smaller, italic, still bold; separator
 *      after every item.
 *
 *   NO prices are ever printed.
 */

import { printHtmlSilent } from "@/lib/kioskMode";

function esc(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

const MODULE_LABEL = {
  RETAIL: "RETAIL",
  RESTAURANT: "RESTAURANT",
  DELIVERY: "DELIVERY",
};

/**
 * @param {object} ctx
 * @param {string} ctx.spot           Station / printer name («ΜΠΑΡ», «ΨΗΣΤΑΡΙΑ»)
 * @param {"RETAIL"|"RESTAURANT"|"DELIVERY"} ctx.module
 * @param {string|null} ctx.tableName  Restaurant only — used for top-right big number
 * @param {string|number|null} ctx.orderNo  Retail/Delivery — top-right big number
 * @param {string|null} ctx.operatorName
 * @param {string|null} ctx.createdAt  ISO datetime; falls back to now
 * @param {Array<{qty:number,description:string,comments?:Array,free_text?:string,is_void?:boolean}>} ctx.items
 * @param {boolean} [ctx.correction]   When true, renders a «ΔΙΟΡΘΩΣΗ ΠΑΡΑΓΓΕΛΙΑΣ»
 *                                     banner under the headers. Items flagged
 *                                     `is_void:true` are rendered with the
 *                                     «ΑΚΥΡΩΘΗΚΕ» prefix and a strike-through.
 */
export function buildKitchenTicketHtml(ctx) {
  const {
    spot = "ΕΚΤΥΠΩΤΗΣ",
    module = "RESTAURANT",
    tableName = null,
    orderNo = null,
    operatorName = null,
    createdAt = null,
    items = [],
    correction = false,
  } = ctx || {};

  // Compact datetime — same format as proforma.
  const { dateStr, timeStr, orderNoStr } = (() => {
    try {
      const d = createdAt ? new Date(createdAt) : new Date();
      const pad = (n) => String(n).padStart(2, "0");
      return {
        dateStr: `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)}`,
        timeStr: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
        orderNoStr: orderNo != null ? String(orderNo) : "",
      };
    } catch {
      return { dateStr: "", timeStr: "", orderNoStr: orderNo != null ? String(orderNo) : "" };
    }
  })();

  // Top-right "big number": Table name for Restaurant, order no otherwise.
  const isRestaurant = module === "RESTAURANT";
  const bigRight =
    isRestaurant && tableName
      ? String(tableName)
      : orderNoStr || "";

  const itemsHtml = (items || [])
    .map((it) => {
      const qty = Number(it.qty || 1);
      const desc = it.description || "";
      const isVoid = !!it.is_void;
      const cArr = Array.isArray(it.comments) ? it.comments : [];
      const commentsHtml = cArr
        .map(
          (c) => `<div style="font-size:16px;font-style:italic;padding-left:10px;color:#222">· ${esc(c.label || c.name || "")}</div>`,
        )
        .join("");
      const freeHtml = it.free_text
        ? `<div style="font-size:16px;font-style:italic;padding-left:10px;color:#222">· ${esc(it.free_text)}</div>`
        : "";
      if (isVoid) {
        return `
        <div style="font-size:20px;font-weight:900;margin-top:3px;letter-spacing:0.03em">
          ΑΚΥΡΩΘΗΚΕ ${qty} × <span style="text-decoration:line-through">${esc(desc)}</span>
        </div>${commentsHtml}${freeHtml}
        <hr class="dotted" />`;
      }
      return `
        <div style="font-size:18px;font-weight:bold;margin-top:3px">
          ${qty} × ${esc(desc)}
        </div>${commentsHtml}${freeHtml}
        <hr class="dotted" />`;
    })
    .join("");

  const correctionBanner = correction
    ? `<div style="text-align:center;font-size:24px;font-weight:900;letter-spacing:0.08em;border:3px solid #000;padding:6px 4px;margin:6px 0;background:#000;color:#fff">ΔΙΟΡΘΩΣΗ ΠΑΡΑΓΓΕΛΙΑΣ</div>`
    : "";

  const html = `<!doctype html>
<html lang="el"><head><meta charset="utf-8"><title>TICKETA — ${esc(spot)}</title>
<style>
  @page { size: 76mm auto; margin: 0; }
  body { font-family: monospace, sans-serif; margin: 0; padding: 4mm; width: 68mm; font-size: 12px; color: #000; line-height: 1.3; }
  .spot { font-size: 22px; font-weight: 900; letter-spacing: 0.05em; text-transform: uppercase; }
  .module { font-size: 12px; font-weight: bold; margin-top: 2px; }
  .big-right { text-align: right; font-size: 26px; font-weight: 900; letter-spacing: 0.05em; line-height: 1; margin-top: 4px; }
  .operator { text-align: right; font-weight: bold; font-size: 12px; margin-top: 4px; }
  .meta-row { display: flex; justify-content: space-between; font-weight: bold; font-size: 12px; margin-top: 4px; font-family: monospace; }
  .meta-row .right { font-weight: bold; }
  .spacer { height: 2.4em; } /* 2 blank rows */
  hr.solid { border: 0; border-top: 1.5px solid #000; margin: 4px 0; }
  hr.dotted { border: 0; border-top: 1px dashed #000; margin: 6px 0; }
</style></head>
<body>
  <div class="spot">${esc(spot)}</div>
  <div class="module">ΤΜΗΜΑ: ${esc(MODULE_LABEL[module] || module)}</div>
  ${bigRight ? `<div class="big-right">${isRestaurant ? `<span style="font-weight:normal;font-size:14px;letter-spacing:0">ΤΡΑΠΕΖΙ </span>` : `<span style="font-weight:normal;font-size:14px;letter-spacing:0">#</span>`}${esc(bigRight)}</div>` : ""}
  ${operatorName ? `<div class="operator">${esc(operatorName)}</div>` : ""}
  <div class="meta-row">
    <span>${esc(dateStr)}${dateStr ? "   " : ""}${esc(timeStr)}</span>
    <span class="right">${orderNoStr ? `#${esc(orderNoStr)}` : ""}</span>
  </div>
  <div class="spacer"></div>
  <hr class="solid" />
  ${correctionBanner}
  ${itemsHtml || '<div style="text-align:center;font-size:11px">(κενή παραγγελία)</div>'}
</body></html>`;

  return { html };
}

/**
 * Print via a hidden iframe (see the comment in
 * ``lib/proforma.js::openProformaWindow`` for the full rationale).
 * We NEVER use window.open here because the caller has always
 * awaited at least one HTTP round-trip before we run — the browser
 * strips the user-activation token during that await and blocks the
 * popup on production HTTPS, causing the «green ✓ αλλά τίποτα δεν
 * τυπώθηκε + κόλλησε το UI» bug that the operators reported.
 */
export function openKitchenTicketWindow({ html }) {
  return printHtmlSilent(html, { afterMs: 300 });
}

/** Convenience — build + print in a single call. */
export function printKitchenTicket(ctx) {
  return openKitchenTicketWindow(buildKitchenTicketHtml(ctx));
}
